import type { Artifact } from './graphql';

export interface EvidenceRow {
  id: string;
  title: string;
  detail?: string;
  meta?: string;
  status?: 'verified' | 'warning' | 'unverified';
}

export function artifactPayloadFrom(artifact: Artifact | null): Record<string, unknown> | null {
  if (!artifact) return null;
  if (typeof artifact.payload === 'object' && artifact.payload !== null) return artifact.payload as Record<string, unknown>;
  try {
    return JSON.parse(artifact.payload) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function artifactKey(artifact: Artifact | null): string {
  const payload = artifactPayloadFrom(artifact);
  if (!artifact) return '';
  const id = payload?.bidMemoVersionId ?? payload?.shredderRunId ?? payload?.normalizationRunId ?? payload?.researchPackId ?? payload?.lboRunId ?? payload?.risksRunId ?? payload?.jobId ?? payload?.version ?? '';
  return `${artifact.entityType}:${artifact.createdAt}:${String(id)}`;
}

function arrayLength(value: unknown): number {
  return Array.isArray(value) ? value.length : 0;
}

function rec(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function arr(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function labelForCitation(value: unknown): string {
  const citation = rec(value);
  return String(citation.label ?? citation.refId ?? 'Evidence ref');
}

function metaForCitation(value: unknown): string {
  const citation = rec(value);
  const parts = [
    citation.sourceType ? String(citation.sourceType) : undefined,
    citation.extractionMethod ? String(citation.extractionMethod) : undefined,
    typeof citation.extractionConfidence === 'number' ? `${Math.round(citation.extractionConfidence * 100)}% confidence` : undefined,
  ].filter(Boolean);
  return parts.join(' · ');
}

function statusForCitation(value: unknown): EvidenceRow['status'] {
  const citation = rec(value);
  if (!citation.refId && !citation.label) return 'unverified';
  if (typeof citation.extractionConfidence === 'number' && citation.extractionConfidence < 0.8) return 'warning';
  return 'verified';
}

function citationDetail(value: unknown): string {
  const citation = rec(value);
  const sourceDoc = citation.sourceDocId ? String(citation.sourceDocId) : 'document';
  return citation.sourceDocId || citation.page ? `Source ${sourceDoc}${citation.page ? ` p.${String(citation.page)}` : ''}` : '';
}

export function extractEvidenceRows(payload: Record<string, unknown> | null): { claims: EvidenceRow[]; citations: EvidenceRow[]; sources: EvidenceRow[] } {
  if (!payload) return { claims: [], citations: [], sources: [] };
  const sections = arr(payload.sections).map(rec);
  const sectionClaims = sections.flatMap((section) => {
    const title = String(section.title ?? 'Section');
    return arr(section.claims).map(rec).map((claim, index): EvidenceRow => {
      const refs = arr(claim.evidenceRefs);
      return {
        id: `section-${title}-${index}-${String(claim.text ?? '')}`,
        title: String(claim.text ?? 'Untitled claim'),
        detail: title,
        meta: `${refs.length} evidence ref${refs.length === 1 ? '' : 's'}`,
        status: refs.length > 0 ? 'verified' : 'unverified',
      };
    });
  });
  const riskClaims = arr(payload.riskRegister).map(rec).map((risk, index): EvidenceRow => {
    const refs = arr(risk.evidenceRefs);
    return {
      id: `risk-${String(risk.id ?? index)}`,
      title: String(risk.claim ?? risk.id ?? 'Risk claim'),
      detail: String(risk.category ?? 'risk'),
      meta: `${String(risk.severity ?? 'unknown')} · ${refs.length} evidence ref${refs.length === 1 ? '' : 's'}`,
      status: refs.length > 0 ? 'verified' : 'unverified',
    };
  });
  const citations = arr(payload.citations).map((citation, index): EvidenceRow => ({
    id: `citation-${String(rec(citation).refId ?? index)}`,
    title: labelForCitation(citation),
    detail: citationDetail(citation),
    meta: metaForCitation(citation),
    status: statusForCitation(citation),
  }));
  const sources = arr(payload.sourceManifest).map((source, index): EvidenceRow => {
    const item = rec(source);
    const confidence = typeof item.confidence === 'number' ? `${Math.round(item.confidence * 100)}% confidence` : undefined;
    return {
      id: `source-${String(item.sourceId ?? index)}`,
      title: String(item.title ?? item.sourceId ?? `Source ${index + 1}`),
      detail: String(item.url ?? ''),
      meta: [item.adapterUsed ? String(item.adapterUsed) : undefined, confidence].filter(Boolean).join(' · '),
      status: item.url ? 'verified' : 'warning',
    };
  });
  return {
    claims: [...sectionClaims, ...riskClaims],
    citations,
    sources,
  };
}

export function summarizeEvidence(payload: Record<string, unknown> | null): { citationCount: number; sourceCount: number; sectionCount: number; claimCount: number } {
  if (!payload) return { citationCount: 0, sourceCount: 0, sectionCount: 0, claimCount: 0 };
  const sections = Array.isArray(payload.sections) ? payload.sections : [];
  const sectionClaims = sections.flatMap((section) => {
    if (typeof section !== 'object' || section === null) return [];
    const claims = (section as { claims?: unknown }).claims;
    return Array.isArray(claims) ? claims : [];
  });
  const riskClaims = Array.isArray(payload.riskRegister) ? payload.riskRegister.length : 0;
  const embeddedCitationCount = sectionClaims.reduce((count, claim) => {
    const value = rec(claim);
    return count + arr(value.evidenceRefs).length + arr(value.citations).length + arr(value.citationIds).length + arr(value.evidence).length;
  }, 0);
  return {
    citationCount: Math.max(arrayLength(payload.citations), embeddedCitationCount),
    sourceCount: arrayLength(payload.sourceManifest),
    sectionCount: sections.length,
    claimCount: sectionClaims.length + riskClaims,
  };
}
